import { useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api, formatBytes, getErrorMessage } from '../api/client';
import type { AccessSeries, CostsResponse, SizemapResponse, StatsOverview } from '../api/types';
import { SizeTreemap } from '../components/SizeTreemap';

function ErrorLine({ error }: { error: unknown }) {
  return <p className="text-sm text-red-600">{getErrorMessage(error)}</p>;
}

function cacheAge(generatedUtc: string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(generatedUtc).getTime()) / 60000));
  return mins === 0 ? 'just now' : `${mins}m ago`;
}

function usd(v: number): string {
  return `$${v.toFixed(2)}`;
}

export function Dashboard() {
  const overview = useQuery({
    queryKey: ['overview'],
    queryFn: () => api<StatsOverview>('/api/admin/stats/overview'),
  });
  const access = useQuery({
    queryKey: ['access', 'day'],
    queryFn: () => api<AccessSeries>('/api/admin/stats/access?groupBy=day'),
  });

  // Storage-usage treemap: per-bucket, server-side KV-cached (~10 min).
  // The selected bucket lives in the URL (?bucket=) so refresh keeps the view.
  const [searchParams, setSearchParams] = useSearchParams();
  const sizeBucket = searchParams.get('bucket') ?? '';
  const activeBucket = sizeBucket || overview.data?.buckets[0]?.logicalName || '';
  const bypassCache = useRef(false);
  const sizemap = useQuery({
    queryKey: ['sizemap', activeBucket],
    queryFn: () => {
      const refresh = bypassCache.current;
      bypassCache.current = false;
      return api<SizemapResponse>(
        `/api/admin/buckets/${encodeURIComponent(activeBucket)}/sizemap${refresh ? '?refresh=1' : ''}`,
      );
    },
    enabled: activeBucket.length > 0,
    staleTime: 5 * 60_000,
  });
  const refreshSizemap = () => {
    bypassCache.current = true;
    void sizemap.refetch();
  };

  // Estimated Cloudflare spend: server-side KV-cached (~6 h).
  const costsBypass = useRef(false);
  const costs = useQuery({
    queryKey: ['costs'],
    queryFn: () => {
      const refresh = costsBypass.current;
      costsBypass.current = false;
      return api<CostsResponse>(`/api/admin/stats/costs${refresh ? '?refresh=1' : ''}`);
    },
    staleTime: 10 * 60_000,
  });
  const refreshCosts = () => {
    costsBypass.current = true;
    void costs.refetch();
  };

  return (
    <div>
      <h1 className="mb-4 text-xl font-bold text-navy">Dashboard</h1>

      {overview.data ? (
        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3">
          {overview.data.buckets.map((b) => (
            <div key={b.logicalName} className="rounded border border-gray-200 bg-white p-4 shadow-sm">
              <div className="font-semibold text-navy">{b.logicalName}</div>
              <div className="text-xs text-gray-500">{b.isPublic ? 'public' : 'private'}</div>
              {b.description ? <div className="mt-1 text-xs text-gray-600">{b.description}</div> : null}
            </div>
          ))}
        </div>
      ) : overview.isLoading ? (
        <p className="text-sm">Loading…</p>
      ) : (
        <ErrorLine error={overview.error} />
      )}

      <div className="mb-2 flex flex-wrap items-center gap-3">
        <h2 className="font-semibold text-navy">Cloudflare cost (estimated)</h2>
        {costs.data?.configured && costs.data.generatedUtc ? (
          <span className="text-xs text-gray-500">
            {costs.data.cached ? `cached ${cacheAge(costs.data.generatedUtc)}` : 'fresh'}
          </span>
        ) : null}
        <button
          className="ml-auto rounded px-2 py-1 text-xs text-navy hover:bg-gray-100 disabled:opacity-50"
          onClick={refreshCosts}
          disabled={costs.isFetching || costs.data?.configured === false}
        >
          {costs.isFetching ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      <div className="mb-6 rounded border border-gray-200 bg-white">
        {costs.data?.configured === false ? (
          <p className="p-3 text-sm text-gray-500">
            Not configured. Create a Cloudflare API token with{' '}
            <span className="font-mono text-xs">Account Analytics: Read</span> and set the worker
            secrets <span className="font-mono text-xs">CF_ANALYTICS_TOKEN</span> and{' '}
            <span className="font-mono text-xs">CF_ACCOUNT_ID</span> (via{' '}
            <span className="font-mono text-xs">wrangler secret put</span>).
          </p>
        ) : costs.data?.lines ? (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
                  <th className="p-2">Line</th>
                  <th className="p-2">Month-to-date usage</th>
                  <th className="p-2">Included in plan</th>
                  <th className="p-2 text-right">Cloudflare</th>
                  <th className="p-2 text-right">Azure</th>
                  <th className="p-2 text-right">AWS</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-2">Workers Paid plan base</td>
                  <td className="p-2 text-gray-500">—</td>
                  <td className="p-2 text-gray-500">—</td>
                  <td className="p-2 text-right">{usd(costs.data.planBaseUsd ?? 0)}</td>
                  <td className="p-2 text-right text-gray-400">{usd(0)}</td>
                  <td className="p-2 text-right text-gray-400">{usd(0)}</td>
                </tr>
                {costs.data.lines.map((l) => (
                  <tr key={`${l.product}:${l.item}`} className="border-b">
                    <td className="p-2">
                      <span className="font-medium">{l.product}</span>
                      <span className="ml-1.5 text-gray-600">{l.item}</span>
                    </td>
                    <td className="p-2 text-gray-600">{l.usage}</td>
                    <td className="p-2 text-xs text-gray-500">{l.included}</td>
                    <td className={`p-2 text-right ${l.costUsd > 0 ? 'font-semibold text-navy' : 'text-gray-400'}`}>
                      {usd(l.costUsd)}
                    </td>
                    <td className={`p-2 text-right ${(l.azureUsd ?? 0) > 0 ? 'text-gray-700' : 'text-gray-400'}`}>
                      {l.azureUsd === null ? '—' : usd(l.azureUsd)}
                    </td>
                    <td className={`p-2 text-right ${(l.awsUsd ?? 0) > 0 ? 'text-gray-700' : 'text-gray-400'}`}>
                      {l.awsUsd === null ? '—' : usd(l.awsUsd)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-semibold text-navy">
                  <td className="p-2" colSpan={3}>
                    Month to date · projected {usd(costs.data.projectedUsd ?? 0)}/mo
                  </td>
                  <td className="p-2 text-right">{usd(costs.data.mtdUsd ?? 0)}</td>
                  <td className="p-2 text-right">{usd(costs.data.azureMtdUsd ?? 0)}</td>
                  <td className="p-2 text-right">{usd(costs.data.awsMtdUsd ?? 0)}</td>
                </tr>
              </tbody>
            </table>
            {costs.data.warnings?.map((w) => (
              <p key={w} className="border-t border-amber-100 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">{w}</p>
            ))}
            <p className="px-3 py-2 text-xs text-gray-400">
              Estimated from usage analytics × published pricing — not an invoice. Egress is bytes
              actually served via the FABS API (direct S3-API downloads not counted); ingress is
              measured at R2 and includes S3-API uploads. Azure/AWS columns price the same usage
              on Blob Storage hot LRS / S3 Standard and Functions / Lambda (consumption, 128 MB,
              east-US PAYG rates incl. free allowances); &quot;—&quot; = no direct equivalent. Not shown:
              Workers Logs (20M events/mo included, then $0.60/M) — Cloudflare exposes no usage API
              for log events.
            </p>
          </>
        ) : costs.isLoading ? (
          <p className="p-3 text-sm">Loading…</p>
        ) : costs.isError ? (
          <div className="p-3"><ErrorLine error={costs.error} /></div>
        ) : null}
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-3">
        <h2 className="font-semibold text-navy">Storage usage</h2>
        <select
          className="rounded border px-2 py-1 text-sm"
          value={activeBucket}
          onChange={(e) => setSearchParams({ bucket: e.target.value }, { replace: true })}
        >
          {!overview.data ? <option value="">{overview.isLoading ? 'Loading…' : 'No buckets'}</option> : null}
          {overview.data?.buckets.map((b) => (
            <option key={b.logicalName} value={b.logicalName}>{b.logicalName}</option>
          ))}
        </select>
        {sizemap.data ? (
          <span className="text-xs text-gray-500">
            {formatBytes(sizemap.data.totalSize)} · {sizemap.data.totalCount.toLocaleString()} objects
            {sizemap.data.cached ? ` · cached ${cacheAge(sizemap.data.generatedUtc)}` : ''}
          </span>
        ) : null}
        <button
          className="ml-auto rounded px-2 py-1 text-xs text-navy hover:bg-gray-100 disabled:opacity-50"
          onClick={refreshSizemap}
          disabled={!activeBucket || sizemap.isFetching}
        >
          {sizemap.isFetching ? 'Scanning…' : 'Refresh'}
        </button>
      </div>
      {sizemap.data?.truncated ? (
        <p className="mb-2 rounded bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
          Large bucket: only the first 50,000 objects were scanned — sizes under-report.
        </p>
      ) : null}
      <div className="mb-6 rounded border border-gray-200 bg-white p-3">
        {sizemap.data ? (
          <SizeTreemap key={activeBucket} root={sizemap.data.root} />
        ) : sizemap.isLoading && activeBucket ? (
          <div className="h-96 animate-pulse rounded bg-gray-100" />
        ) : sizemap.isError ? (
          <ErrorLine error={sizemap.error} />
        ) : (
          <p className="text-sm text-gray-400">No bucket selected.</p>
        )}
      </div>

      <h2 className="mb-2 font-semibold text-navy">Traffic (last 30 days)</h2>
      <div className="h-72 rounded border border-gray-200 bg-white p-3">
        {access.data ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={access.data.series.map((s) => ({
                name: s.bucket,
                egress: s.egress_bytes,
                ingress: s.ingress_bytes,
              }))}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={72} tickFormatter={(v: number) => formatBytes(v)} />
              <Tooltip formatter={(v) => formatBytes(Number(v))} />
              <Legend />
              <Bar dataKey="egress" name="Egress (out)" fill="#282a67" />
              <Bar dataKey="ingress" name="Ingress (in)" fill="#16a34a" />
            </BarChart>
          </ResponsiveContainer>
        ) : access.isLoading ? (
          <p className="text-sm">Loading…</p>
        ) : (
          <ErrorLine error={access.error} />
        )}
      </div>
    </div>
  );
}
